import { OVERWATCH_CONE_DEGREES } from '../../engine/overwatch';
import { BlastOverlay, ConeOverlay, BeamOverlay, ArcOverlay, SpawnedOverlay } from '../BattlefieldOverlays';
import { useAreaPreviews } from './useAreaPreviews';
import { EMPTY } from './panelDefaults';
import { avoidChip } from '../../engine/areaShapes';

/** The flamer's cone, when there is one to lay. */
function ConeTemplate({ cone, camera, sizeHint, avoid }) {
  if (!cone.aim) return null;
  const { apex, bearing, length } = cone.aim;
  return (
    <ConeOverlay
      {...{ apex, bearing, length, camera, sizeHint, avoid }}
      degrees={cone.degrees}
      caught={cone.caught}
      caughtAt={cone.caughtAt}
    />
  );
}

/** The chain the lightning would walk. */
function ArcTemplate({ arc, reach, camera, sizeHint }) {
  if (!arc.preview) return null;
  return (
    <ArcOverlay
      primary={arc.preview.primary}
      reach={reach}
      chain={arc.chainAt}
      camera={camera}
      caught={arc.caught}
      sizeHint={sizeHint}
    />
  );
}

/** The beam's lance of ground. */
function BeamTemplate({ beam, camera, sizeHint, avoid }) {
  if (!beam.aim) return null;
  const { apex, bearing, length } = beam.aim;
  return (
    <BeamOverlay
      {...{ apex, bearing, length, camera, sizeHint, avoid }}
      caught={beam.caught}
      caughtAt={beam.caughtAt}
      halfWidthM={beam.halfWidth}
    />
  );
}

/** One wedge per declared kill zone, whoever holds it. */
function OverwatchZones({ zones, camera }) {
  return zones.map((z) => (
    <ConeOverlay
      key={'overwatch-' + z.id}
      apex={z.apex}
      bearing={z.bearing}
      length={z.length}
      degrees={OVERWATCH_CONE_DEGREES}
      camera={camera}
      caught={EMPTY}
      testId={'overwatch-zone-' + z.id}
    />
  ));
}

/**
 * The selected weapon's templates on the map - blast, cone, arc, the
 * spawned template, the beam - and every declared overwatch zone, each
 * with who it would catch. Under the markers: it is ground, not a marker.
 *
 * `panel` is BattlefieldPanel's props with defaults; `map` its live state.
 */
export default function PanelTemplates({ panel, map }) {
  const { blast, cone, beam, arc, spawn, overwatch } = useAreaPreviews({
    ...panel,
    solids: map.solids,
    tokenList: map.tokenList,
    names: map.names,
    line: map.line,
  });
  const { camera } = map;
  const sizeHint = panel.areaSizeHint || undefined;
  // Each victim's avoid test, before the shot (T2): the primary's, and the spawned template's own.
  const avoid = avoidChip(panel.area);
  return (
    <>
      <BlastOverlay
        centre={blast.centre}
        radius={blast.radius}
        camera={camera}
        caught={blast.caught}
        caughtAt={blast.caughtAt}
        sizeHint={sizeHint}
        avoid={avoid}
      />
      <ConeTemplate cone={cone} camera={camera} sizeHint={sizeHint} avoid={avoid} />
      <ArcTemplate arc={arc} reach={panel.area?.reach} camera={camera} sizeHint={sizeHint} />
      {spawn && (
        <SpawnedOverlay
          outlines={spawn.outlines}
          caughtAt={spawn.caughtAt}
          camera={camera}
          avoid={avoidChip(panel.area?.spawn)}
        />
      )}
      <OverwatchZones zones={overwatch} camera={camera} />
      <BeamTemplate beam={beam} camera={camera} sizeHint={sizeHint} avoid={avoid} />
    </>
  );
}
